// AGC: useRBAC - Hook for role-based access control
// AGC: Provides permission checking and role information for current user

import { useMemo } from 'react'
import { useAuth } from './useAuth'
import type { FarmRole, FarmPermission } from '../types/rbac'
import {
  ROLE_INFO,
  hasPermission as checkPermission,
  getRolePermissions,
  canRoleManage,
  getRoleLevel,
} from '../types/rbac'

/**
 * AGC: RBAC hook return type
 */
export interface RBACResult {
  role: FarmRole | null
  permissions: FarmPermission | null
  roleInfo: typeof ROLE_INFO[FarmRole] | null
  hasPermission: (permission: keyof FarmPermission) => boolean
  canManageRole: (targetRole: FarmRole) => boolean
  roleLevel: number
  isOwner: boolean
  isManager: boolean
  isAgronomist: boolean
  isFinancial: boolean
  isWorker: boolean
  isViewer: boolean
  isLoading: boolean
}

/**
 * AGC: Hook to get current user's role and permissions
 * AGC: Integrates with existing useAuth hook
 * AGC: TODO: Extend to support farm-specific roles when multi-farm is implemented
 */
export const useRBAC = (): RBACResult => {
  const { user, isLoading } = useAuth()

  return useMemo(() => {
    // AGC: Loading state
    if (isLoading || !user) {
      return {
        role: null,
        permissions: null,
        roleInfo: null,
        hasPermission: () => false,
        canManageRole: () => false,
        roleLevel: 0,
        isOwner: false,
        isManager: false,
        isAgronomist: false,
        isFinancial: false,
        isWorker: false,
        isViewer: false,
        isLoading,
      }
    }

    // AGC: Read farm-scoped role from user profile (set by invite accept / role change)
    // AGC: Falls back to old role mapping for users who don't have farmRole yet.
    // AGC: v3.9.35 — self-signup fallback. Accounts created without invitation carry
    // AGC: role='user' with no parentManagerId/createdBy. Backend mapLegacyToFarmRole
    // AGC: ('user', null, isTeamMember=false) returns 'owner' (permissions.js:222) but the
    // AGC: prior frontend fallback always defaulted role='user' to 'worker', producing a
    // AGC: brief window after signup where useRBAC reported the wrong role before the first
    // AGC: authed backend call wrote farmRole. Mirror the backend rule here: role='user'
    // AGC: with a loaded profile and no team signals → 'owner'. Null profile still falls
    // AGC: through to the conservative 'worker' default so RBAC-gated UI isn't enabled
    // AGC: optimistically on unloaded state.
    const profileFarmRole = (user.profile as Record<string, any>)?.farmRole as FarmRole | undefined
    const userRole = user.profile?.role || 'user'
    const hasProfile = Boolean(user.profile)
    const isTeamMember = Boolean(user.profile?.parentManagerId || user.profile?.createdBy)
    const farmRole: FarmRole = profileFarmRole
      || (userRole === 'admin' || userRole === 'manager'
        ? 'owner'
        : userRole === 'submanager'
          ? 'manager'
          : userRole === 'user' && hasProfile && !isTeamMember
            ? 'owner'
            : 'worker')

    const permissions = getRolePermissions(farmRole)
    const roleInfo = ROLE_INFO[farmRole]
    const level = getRoleLevel(farmRole)

    return {
      role: farmRole,
      permissions,
      roleInfo,
      hasPermission: (permission: keyof FarmPermission) => 
        checkPermission(farmRole, permission),
      canManageRole: (targetRole: FarmRole) => 
        canRoleManage(farmRole, targetRole),
      roleLevel: level,
      isOwner: farmRole === 'owner',
      isManager: farmRole === 'manager',
      isAgronomist: farmRole === 'agronomist',
      isFinancial: farmRole === 'financial',
      isWorker: farmRole === 'worker',
      isViewer: farmRole === 'viewer',
      isLoading: false,
    }
  }, [user, isLoading])
}

/**
 * AGC: Hook to check a specific permission
 * AGC: Convenience wrapper for common permission checks
 */
export const useHasPermission = (permission: keyof FarmPermission): boolean => {
  const { hasPermission } = useRBAC()
  return hasPermission(permission)
}

/**
 * AGC: Hook to check if user can perform task operations
 */
export const useTaskPermissions = () => {
  const rbac = useRBAC()
  
  return useMemo(() => ({
    canCreate: rbac.hasPermission('canCreateTask'),
    canEdit: rbac.hasPermission('canEditTask'),
    canDelete: rbac.hasPermission('canDeleteTask'),
    canAssign: rbac.hasPermission('canAssignTask'),
    canUpdateStatus: rbac.hasPermission('canUpdateTaskStatus'),
  }), [rbac])
}

/**
 * AGC: Hook to check if user can perform machine operations
 */
export const useMachinePermissions = () => {
  const rbac = useRBAC()
  
  return useMemo(() => ({
    canCreate: rbac.hasPermission('canCreateMachine'),
    canEdit: rbac.hasPermission('canEditMachine'),
    canDelete: rbac.hasPermission('canDeleteMachine'),
    canAssign: rbac.hasPermission('canAssignMachine'),
    canViewCosts: rbac.hasPermission('canViewMachineCosts'),
  }), [rbac])
}

/**
 * AGC: Hook to check if user can perform team operations
 */
export const useTeamPermissions = () => {
  const rbac = useRBAC()
  
  return useMemo(() => ({
    canInvite: rbac.hasPermission('canInviteUser'),
    canRemove: rbac.hasPermission('canRemoveUser'),
    canChangeRoles: rbac.hasPermission('canChangeRoles'),
    canView: rbac.hasPermission('canViewTeam'),
  }), [rbac])
}

export default useRBAC
